import React, { Suspense, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LazyMotion, domMax } from "framer-motion";
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import Layout from '@/Layout';
import { CookieProvider } from '@/contexts/CookieContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { CookieBanner } from '@/components/privacy/CookieBanner';
import ProtectedRoute from '@/components/ProtectedRoute';
import { PepiteLoader } from '@/components/ui/PepiteLoader';
import { useSessionTracking } from '@/hooks/useSessionTracking';
import { useAndroidBackButton } from '@/hooks/useAndroidBackButton';
import { initMobileStorage } from '@/utils/mobileStorage';

const SessionTracker = () => {
    useSessionTracking();
    useAndroidBackButton();
    return null;
};

// Lazy loading pages for better initial load performance
const LoginPage = React.lazy(() => import('@/pages/auth/Login'));
const RegisterPage = React.lazy(() => import('@/pages/auth/Register'));
const CheckEmailPage = React.lazy(() => import('@/pages/auth/CheckEmail'));
const VerifyEmailPage = React.lazy(() => import('@/pages/VerifyEmail'));
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPassword'));
const UnsubscribePage = React.lazy(() => import('@/pages/Unsubscribe'));

const HomePage = React.lazy(() => import('@/pages/Home'));
const SwipePage = React.lazy(() => import('@/pages/Swipe'));
const MatchesPage = React.lazy(() => import('@/pages/Matches'));
const ProfilPage = React.lazy(() => import('@/pages/Profil'));
const HistoriquePage = React.lazy(() => import('@/pages/Historique'));
const CategoriesPage = React.lazy(() => import('@/pages/Categories'));
const RecherchePage = React.lazy(() => import('@/pages/Recherche'));
const ProductDetailPage = React.lazy(() => import('@/pages/ProductDetail'));
const BrandPage = React.lazy(() => import('@/pages/BrandPage'));
const LegalNoticesPage = React.lazy(() => import('@/pages/LegalNotices'));
const PrivacyPolicyPage = React.lazy(() => import('@/pages/PrivacyPolicy'));
const AboutUsPage = React.lazy(() => import('@/pages/AboutUs'));
const FAQPage = React.lazy(() => import('@/pages/FAQ'));
const ContactPage = React.lazy(() => import('@/pages/Contact'));

// Admin Pages Lazy Loading
const AdminScraperPage = React.lazy(() => import('@/pages/admin/AdminScraper').then(module => ({ default: module.AdminScraperPage })));
const ScraperSitesManager = React.lazy(() => import('@/pages/admin/ScraperSitesManager').then(module => ({ default: module.ScraperSitesManager })));
const ScrapedProductsPage = React.lazy(() => import('@/pages/admin/ScrapedProducts').then(module => ({ default: module.ScrapedProductsPage })));
const AlgorithmConfigPage = React.lazy(() => import('@/pages/admin/AlgorithmConfig'));
const StatsPage = React.lazy(() => import('@/pages/admin/Stats'));
const AdminCategoriesPage = React.lazy(() => import('@/pages/admin/AdminCategories'));
const AdminColorsPage = React.lazy(() => import('@/pages/admin/AdminColors'));
const PosterStudioPage = React.lazy(() => import('@/pages/admin/PosterStudio'));
const CarouselAdminPage = React.lazy(() => import('@/pages/admin/CarouselAdmin').then(m => ({ default: m.CarouselAdmin })));
const PersonalShopperAdminPage = React.lazy(() => import('@/pages/admin/PersonalShopperAdmin'));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
            gcTime: 1000 * 60 * 10,  // Cache is kept for 10 minutes (garbage collection)
            refetchOnWindowFocus: false, // Don't refetch just because I clicked alt-tab
            retry: 1
        }
    }
});

const LoadingFallback = () => (
    <div className="h-[100dvh] w-full flex flex-col items-center justify-center bg-gray-50 dark:bg-[hsl(222,25%,7%)]" aria-busy="true" aria-label="Chargement en cours">
        <PepiteLoader className="w-24 h-24 mb-6" />
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">Chargement de l'application...</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">Préparation des pépites ✨</p>
    </div>
);

import { Toaster } from 'sonner';

const App: React.FC = () => {
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            initMobileStorage().finally(() => {
                SplashScreen.hide().catch(() => {});
            });
        }
    }, []);

    return (
        <LazyMotion features={domMax}>
            <ThemeProvider>
                <QueryClientProvider client={queryClient}>
                    <CookieProvider>
                        <Router>
                            <SessionTracker />
                            <Routes>
                                {/* Public Routes */}
                                <Route path="/login" element={<Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense>} />
                                <Route path="/register" element={<Suspense fallback={<LoadingFallback />}><RegisterPage /></Suspense>} />
                                <Route path="/check-email" element={<Suspense fallback={<LoadingFallback />}><CheckEmailPage /></Suspense>} />
                                <Route path="/verify-email" element={<Suspense fallback={<LoadingFallback />}><VerifyEmailPage /></Suspense>} />
                                <Route path="/forgot-password" element={<Suspense fallback={<LoadingFallback />}><ForgotPasswordPage /></Suspense>} />
                                <Route path="/reset-password" element={<Suspense fallback={<LoadingFallback />}><ResetPasswordPage /></Suspense>} />
                                <Route path="/unsubscribe" element={<Suspense fallback={<LoadingFallback />}><UnsubscribePage /></Suspense>} />

                            {/* Protected Routes Wrapped in Layout */}
                            <Route
                                path="*"
                                element={
                                    <Layout>
                                        <Suspense fallback={<LoadingFallback />}>
                                            <Routes>
                                                <Route path="/" element={<Navigate to="/home" replace />} />
                                                <Route path="/home" element={<HomePage />} />
                                                <Route path="/swipe" element={<SwipePage />} />
                                                <Route path="/matches" element={<MatchesPage />} />
                                                <Route path="/profile" element={<ProtectedRoute><ProfilPage /></ProtectedRoute>} />
                                                <Route path="/history" element={<HistoriquePage />} />
                                                <Route path="/categories" element={<CategoriesPage />} />
                                                <Route path="/search" element={<RecherchePage />} />
                                                <Route path="/product" element={<ProductDetailPage />} />
                                                <Route path="/brand/:slug" element={<BrandPage />} />
                                                <Route path="/mentions-legales" element={<LegalNoticesPage />} />
                                                <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
                                                <Route path="/a-propos" element={<AboutUsPage />} />
                                                <Route path="/faq" element={<FAQPage />} />
                                                <Route path="/contact" element={<ContactPage />} />
                                                <Route path="/shopper" element={<PersonalShopperAdminPage />} />

                                                {/* Admin Routes */}
                                                <Route path="/admin" element={<ProtectedRoute adminOnly><Navigate to="/admin/scraper" replace /></ProtectedRoute>} />
                                                <Route path="/admin/scraper" element={<ProtectedRoute adminOnly><AdminScraperPage /></ProtectedRoute>} />
                                                <Route path="/admin/sites" element={<ProtectedRoute adminOnly><ScraperSitesManager /></ProtectedRoute>} />
                                                <Route path="/admin/products" element={<ProtectedRoute adminOnly><ScrapedProductsPage /></ProtectedRoute>} />
                                                <Route path="/admin/stats" element={<ProtectedRoute adminOnly><StatsPage /></ProtectedRoute>} />
                                                <Route path="/admin/algorithm" element={<ProtectedRoute adminOnly><AlgorithmConfigPage /></ProtectedRoute>} />
                                                <Route path="/admin/categories" element={<ProtectedRoute adminOnly><AdminCategoriesPage /></ProtectedRoute>} />
                                                <Route path="/admin/colors" element={<ProtectedRoute adminOnly><AdminColorsPage /></ProtectedRoute>} />
                                                <Route path="/admin/posters" element={<ProtectedRoute adminOnly><PosterStudioPage /></ProtectedRoute>} />
                                                <Route path="/admin/carousel" element={<ProtectedRoute adminOnly><CarouselAdminPage /></ProtectedRoute>} />

                                                {/* Catch all within Layout */}
                                                <Route path="*" element={<Navigate to="/home" replace />} />
                                            </Routes>
                                        </Suspense>
                                    </Layout>
                                }
                            />
                        </Routes>
                        <CookieBanner />
                        <Toaster richColors />
                    </Router>
                </CookieProvider>
            </QueryClientProvider>
        </ThemeProvider>
        </LazyMotion>
    );
};

export default App;
